import React from "react";
import Highcharts from "highcharts";
import axios from "axios";
import moment from "moment";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import config from "../config";
import "../styles/layout.css";
import "./Home.css";

import formatBandwidth from "../utils/formatBandwidth";

const baseChartOptions = {
  time: { useUTC: true },
  credits: { enabled: false },
  chart: {
    type: "spline",
    backgroundColor: "transparent",
    style: { fontFamily: "inherit" },
  },
  legend: { enabled: true },
  xAxis: {
    type: "datetime",
    gridLineWidth: 1,
    dateTimeLabelFormats: {
      minute: "%H:%M",
      hour: "%H:%M",
      day: "%e. %b",
    },
  },
  plotOptions: {
    spline: {
      lineWidth: 2,
      marker: { enabled: false, radius: 3 },
      states: { hover: { lineWidth: 2.5 } },
    },
  },
};

class Home extends React.Component {
  connectionChart = null;
  networkChart = null;
  refreshTimer = null;

  state = {
    loading: true,
    error: "",
    rangeLabel: "",
    maxCcu: 0,
    maxOut: 0,
    maxIn: 0,
  };

  componentDidMount() {
    this.createConnectionChart([]);
    this.createNetworkChart([], []);
    this.loadDashboard();
    this.refreshTimer = setInterval(this.loadDashboard, 60 * 1000);
  }

  componentWillUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    this.destroyCharts();
  }

  destroyCharts = () => {
    if (this.connectionChart) {
      this.connectionChart.destroy();
      this.connectionChart = null;
    }
    if (this.networkChart) {
      this.networkChart.destroy();
      this.networkChart = null;
    }
  };

  getLastHourRange = () => {
    const end = moment();
    const start = moment().subtract(1, "hours");
    return {
      range: "lasthour",
      group: 0,
      wowza: 0,
      start: start.valueOf(),
      end: end.valueOf(),
      label: `${start.format("HH:mm")} – ${end.format("HH:mm")}`,
    };
  };

  maxFromSeries = (seriesList) => {
    let max = 0;
    (seriesList || []).forEach((serie) => {
      (serie.data || []).forEach((point) => {
        const y = Array.isArray(point) ? Number(point[1]) : Number(point?.y);
        if (Number.isFinite(y) && y > max) max = y;
      });
    });
    return max;
  };

  createConnectionChart = (series) => {
    const el = document.getElementById("home-connection-chart");
    if (!el) return;

    if (this.connectionChart) {
      this.connectionChart.destroy();
      this.connectionChart = null;
    }

    this.connectionChart = Highcharts.chart("home-connection-chart", {
      ...baseChartOptions,
      title: { text: null },
      tooltip: {
        shared: true,
        pointFormat:
          '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> CCU<br/>',
        valueDecimals: 0,
      },
      yAxis: {
        title: { text: "Connection (CCU)" },
        allowDecimals: false,
        min: 0,
      },
      series: (series || []).map((s, index) => ({
        name: s.name || "Connection",
        data: Array.isArray(s.data) ? s.data : [],
        color: index === 0 ? "#2563eb" : undefined,
      })),
    });
  };

  createNetworkChart = (byteIn, byteOut) => {
    const el = document.getElementById("home-network-chart");
    if (!el) return;

    if (this.networkChart) {
      this.networkChart.destroy();
      this.networkChart = null;
    }

    const inSeries = (byteIn || [])[0] || { name: "In", data: [] };
    const outSeries = (byteOut || [])[0] || { name: "Out", data: [] };

    this.networkChart = Highcharts.chart("home-network-chart", {
      ...baseChartOptions,
      title: { text: null },
      tooltip: {
        shared: true,
        pointFormatter: function () {
          return `<span style="color:${this.series.color}">${
            this.series.name
          }</span>: <b>${formatBandwidth(this.y)}</b><br/>`;
        },
      },
      yAxis: {
        title: { text: "Bandwidth" },
        min: 0,
        labels: {
          formatter: function () {
            return formatBandwidth(this.value);
          },
        },
      },
      series: [
        {
          name: outSeries.name ? `Out · ${outSeries.name}` : "Byte Out",
          data: Array.isArray(outSeries.data) ? outSeries.data : [],
          color: "#16a34a",
        },
        {
          name: inSeries.name ? `In · ${inSeries.name}` : "Byte In",
          data: Array.isArray(inSeries.data) ? inSeries.data : [],
          color: "#f59e0b",
        },
      ],
    });
  };

  loadDashboard = () => {
    const range = this.getLastHourRange();
    const body = {
      group: range.group,
      wowza: range.wowza,
      start: range.start,
      end: range.end,
      range: range.range,
    };

    this.setState({ loading: true, error: "", rangeLabel: range.label });

    if (this.connectionChart) this.connectionChart.showLoading("Loading...");
    if (this.networkChart) this.networkChart.showLoading("Loading...");

    Promise.all([
      axios.post(`${config.host}/report/max/concurrent`, body),
      axios.post(`${config.host}/report/max/network`, body),
    ])
      .then(([concurrentRes, networkRes]) => {
        const connectionSeries = concurrentRes?.data?.dataset || [];
        const byteIn = networkRes?.data?.dataset?._byteIn || [];
        const byteOut = networkRes?.data?.dataset?._byteOut || [];

        this.createConnectionChart(connectionSeries);
        this.createNetworkChart(byteIn, byteOut);

        this.setState({
          loading: false,
          maxCcu: this.maxFromSeries(connectionSeries),
          maxOut: this.maxFromSeries(byteOut),
          maxIn: this.maxFromSeries(byteIn),
        });
      })
      .catch((err) => {
        const message =
          (err?.response &&
            (typeof err.response.data === "string"
              ? err.response.data
              : err.response.data?.message || err.response.statusText)) ||
          err.message ||
          "Failed to load dashboard";

        this.createConnectionChart([]);
        this.createNetworkChart([], []);

        this.setState({
          loading: false,
          error: message,
          maxCcu: 0,
          maxOut: 0,
          maxIn: 0,
        });
      });
  };

  render() {
    const { loading, error, rangeLabel, maxCcu, maxOut, maxIn } = this.state;

    return (
      <div className="app-shell">
        <Navbar />
        <main className="app-main">
          <div className="home-dashboard">
            <div className="home-dashboard__header">
              <div>
                <h1 className="home-dashboard__title">Dashboard</h1>
                <p className="home-dashboard__subtitle">
                  Connection &amp; network for the last 1 hour
                  {rangeLabel ? ` · ${rangeLabel}` : ""}
                </p>
              </div>
              <button
                type="button"
                className="home-dashboard__refresh"
                onClick={this.loadDashboard}
                disabled={loading}
              >
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>

            {error ? (
              <div className="home-dashboard__error">{error}</div>
            ) : null}

            <div className="home-dashboard__stats">
              <div className="home-stat">
                <span className="home-stat__label">Peak CCU</span>
                <strong className="home-stat__value">
                  {Number(maxCcu).toLocaleString()}
                </strong>
              </div>
              <div className="home-stat">
                <span className="home-stat__label">Peak Out</span>
                <strong className="home-stat__value">{formatBandwidth(maxOut)}</strong>
              </div>
              <div className="home-stat">
                <span className="home-stat__label">Peak In</span>
                <strong className="home-stat__value">{formatBandwidth(maxIn)}</strong>
              </div>
            </div>

            <div className="home-dashboard__grid">
              <section className="home-panel">
                <div className="home-panel__header">
                  <h2 className="home-panel__title">Connection</h2>
                  <p className="home-panel__subtitle">
                    Max concurrent viewers (CCU)
                  </p>
                </div>
                <div className="home-panel__body">
                  <div id="home-connection-chart" className="home-chart" />
                </div>
              </section>

              <section className="home-panel">
                <div className="home-panel__header">
                  <h2 className="home-panel__title">Network</h2>
                  <p className="home-panel__subtitle">
                    Bandwidth in / out (last 1 hour)
                  </p>
                </div>
                <div className="home-panel__body">
                  <div id="home-network-chart" className="home-chart" />
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
}

export default Home;
